import { api, call, type CallFailure } from './api.js';
import type { IntakeMeta } from '../../../api/src/catalog/intake.js';

/**
 * THE INTAKE VOCABULARY, REMEMBERED FOR THE FIRST FRAME (0.1.526).
 *
 * The maintainer, 2 Oct 2026: the item detail "shows the first half on opening but has a little
 * loading message for everything within the info section … all of the sudden, all of the info gets
 * loaded in" — the Info tab's field grid waited on `intakeMeta` (fields, categories, locations),
 * asked fresh on every open, and arrived as a jump. Four screens ask for it (the item's Info panel,
 * the Items sheet, the filter sheet, bulk edit); every answer now lands here, and the Info panel
 * PAINTS from the last one while it asks again.
 *
 * ⚠️ STALE-WHILE-REVALIDATE, NEVER STALE-INSTEAD. Every mount still asks the server and replaces the
 * first frame with the answer; this only decides what the first frame shows. Invalidating on each
 * vocabulary edit was the alternative and was rejected: the vocabularies are edited from at least
 * three places (structure-menu, structure-manage, the rack screen), and a memo that depends on
 * every one of them remembering to clear it goes stale the first time one forgets. Staleness here is
 * bounded by one round trip instead. In memory only — never persisted (read-cache.ts says why a
 * persisted read is a security matter); cleared on sign-out (app.tsx). It is tenant configuration
 * — field names and option lists — not personal data.
 */
let last: IntakeMeta | null = null;

/** The last answer this tab received, or null — for a first frame only. */
export function lastIntakeMeta(): IntakeMeta | null {
  return last;
}

/** Ask the server (through `call`, so a failure is classified and ledgered), and remember a success. */
export async function readIntakeMeta(): Promise<{ ok: true; data: IntakeMeta } | { ok: false; failure: CallFailure }> {
  const result = await call('intakeMeta', () => api.intakeMeta.query());
  if (result.ok) last = result.data;
  return result;
}

export function forgetIntakeMeta(): void {
  last = null;
}
